import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import { CmLongLoading, CmScrollBox } from '@/components/uiParts';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { MenuManagementCategoriesForm } from '@/pages/menu-managements/categories/components/Form';
import { useMenuManagementCategoryForm } from '@/pages/menu-managements/categories/hooks';
import { CanNotUpdateMenuManagementNotice } from '@/pages/menu-managements/components/Alert/CanNotUpdateMenuManagementNotice';
import { SearchItemSelectModal } from '@/pages/menu-managements/components/SearchItemSelectModal';
import { MenuManagementEditButtonArea } from '@/pages/menu-managements/components/TopButtonArea';
import { routes } from '@/router';
import { useMenuManagementDeleteBrandCategory } from '@/services/BrandCategory/hooks/delete/useMenuManagementDeleteBrandCategory';

const MenuManagementCategoriesEditPage: React.FC = (): JSX.Element => {
  const { id: urlParamsId } = useParams<{ id: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();

  const {
    associateBrandSelectInput,
    titleJa,
    memo,
    error,
    searchItems,
    selectedItems,
    searchItemName,
    isSearching,
    targetsHeight,
    initialStringifyFormState,
    isLoading,
    isCategoryItemModalOpen,
    setIsLoading,
    handleInputName,
    handleInputMemo,
    handleOnDragEnd,
    handleSearchItemNameInput,
    handleAddSelectItems,
    handleDeleteDragItem,
    initializeEditPageState,
    handleUpdate,
    handleClickMultipleItemSelect,
    handleCloseCategoryItemModal,
    handleSetMultipleItems,
  } = useMenuManagementCategoryForm();

  const isEnabledTitle = !error.titleJa.isError;
  const isEnabledMemo = !error.memo.isError;
  const isEdit =
    initialStringifyFormState !==
    JSON.stringify([titleJa, memo, selectedItems]);
  const isDisabledSubmitButton = [isEnabledTitle, isEnabledMemo, isEdit].some(
    (enabled) => !enabled
  );

  const listUrl = routes.menuManagementCategories.path;

  const handleDelete = () => {
    (async () => {
      try {
        setIsLoading(true);
        await useMenuManagementDeleteBrandCategory(urlParamsId);
        setIsLoading(false);
        alert('カテゴリを削除しました。');
        history.push(listUrl);
      } catch (error) {
        setIsLoading(false);
        if (error instanceof HttpError && error.status === 400) {
          return alert(error.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error });
      }
    })();
  };

  useEffect(() => {
    (async () => {
      await initializeEditPageState(urlParamsId);
    })();
  }, [urlParamsId]);

  return (
    <PageLayout
      title={routes.menuManagementCategoriesEdit.title}
      associateBrandSelectInput={{
        ...associateBrandSelectInput,
        isDisabled: true,
      }}
      alert={<CanNotUpdateMenuManagementNotice pageName={'カテゴリ'} />}
    >
      <MenuManagementEditButtonArea
        name="カテゴリ"
        isOpenDeleteOpen={isOpen}
        handleOpenDeleteModal={onOpen}
        handleCloseDeleteModal={onClose}
        {...{
          listUrl,
          urlParamsId,
          isDisabledSubmitButton,
          handleUpdate,
          handleDelete,
        }}
      />
      <CmScrollBox height={`calc(100vh - ${targetsHeight}px)`}>
        <MenuManagementCategoriesForm
          {...{
            associateBrandSelectInput,
            titleJa,
            memo,
            handleInputName,
            handleInputMemo,
            error,
            searchItems,
            selectedItems,
            handleOnDragEnd,
            searchItemName,
            handleSearchItemNameInput,
            handleAddSelectItems,
            handleDeleteDragItem,
            isSearching,
            handleClickMultipleItemSelect,
          }}
          brandSelectValue={associateBrandSelectInput.value?.value ?? ''}
        />
      </CmScrollBox>
      {isLoading && <CmLongLoading />}
      <SearchItemSelectModal
        title="商品の選択"
        emptyMessage="選択できる商品がありませんでした"
        isLoading={isSearching}
        isOpen={isCategoryItemModalOpen}
        onClose={handleCloseCategoryItemModal}
        selectedItems={selectedItems}
        searchItems={searchItems}
        handleSetMultipleItems={handleSetMultipleItems}
      />
    </PageLayout>
  );
};

export default MenuManagementCategoriesEditPage;
